import React, { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { initializeCart, toggleAuthentication } from "../../redux/hejalSlice";

// Token Calculation Function
const calculateToken = (otp, algoName) => {
  const otpNum = Number(otp);
  if (isNaN(otpNum)) {
    console.error("Invalid OTP format");
    return null;
  }

  // Retrieve multipliers from environment variables
  const multipliersEnv = process.env.REACT_APP_MULTIPLIERS;
  if (!multipliersEnv) {
    return;
  }

  // Convert the string into an object
  const multipliers = multipliersEnv.split(",").reduce((acc, item) => {
    const [key, value] = item.split(":");
    acc[key] = Number(value);
    return acc;
  }, {});

  return multipliers[algoName] ? otpNum * multipliers[algoName] : null;
};

const NewPassword = () => {
  const location = useLocation();
  const { email, otp, algoName } = location.state || {};
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState();

  // Handle password validation
  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    return passwordRegex.test(password);
  };

  const handleVerifyNewPassword = useCallback(
    async (e) => {
      setLoading(true);
      e.preventDefault();

      if (!email || !otp || !algoName) {
        toast.error("Missing required information. Please retry.");
        return;
      }

      if (!validatePassword(password)) {
        setErrorMsg(
          "Password must be at least 6 characters long, contain an uppercase letter, a lowercase letter, a number, and a special character."
        );
        toast.error("Invalid password format.");
        return;
      }

      if (password !== confirmPassword) {
        setErrorMsg("Passwords do not match!");
        toast.error("Passwords do not match!");
        return;
      }

      console.log("Calculating token with OTP:", otp, "and algoName:", algoName);

      const token = calculateToken(otp, algoName);
      if (!token) {
        toast.error("Error calculating security token. Please retry.");
        return;
      }

      try {
        const response = await ApiService({
          method: "PUT",
          endpoint: apiPaths.VerifyPassword,
          data: {
            email,
            otp,
            newPassword: password,
          },
          headers: {
            token: token,
          },
        });

        if (response.errorCode) {
          toast.error(response.message || "Failed to change password!");
          setErrorMsg(response.message || "Failed to change password");
        } else {
          dispatch(toggleAuthentication());
          if (response.user?.cart) {
            dispatch(initializeCart(response.user.cart));
          }
          toast.success("Password changed successfully!");
          setTimeout(() => navigate("/signin"), 2000);
        }
      } catch (error) {
        console.error("Password change failed:", error);
        toast.error(
          error?.response?.data?.message || "Something went wrong, try again."
        );
        setErrorMsg("Server error. Please try again later.");
      } finally {
        setLoading(false)
      }
    },
    [email, otp, algoName, password, confirmPassword, dispatch, navigate]
  );

  return (
    <div className="w-full h-screen flex items-center justify-center bg-[#efefef]">
      <form
        className="px-6 max-w-[500px] min-h-[350px] bg-white py-10 w-full flex flex-col overflow-y-scroll scrollbar-thin scrollbar-thumb-primeColor shadow-lg rounded-md"
        onSubmit={handleVerifyNewPassword}
      >
        <h1 className="font-semibold text-[24px] mb-4">New Password</h1>
        <div className="flex flex-col gap-3">
          {/* New Password Field */}
          <label className="font-medium text-[14px]">New Password</label>
          <div className="relative">
            <input
              onChange={(e) => {
                setPassword(e.target.value);
                setErrorMsg("");
              }}
              value={password}
              className="w-full h-12 bg-transparent border-b border-gray-400 outline-none px-4 pr-10"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
            />
            <span
              className="absolute right-3 top-3 cursor-pointer text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </span>
          </div>

          {/* Confirm Password Field */}
          <label className="font-medium text-[14px]">Confirm Password</label>
          <div className="relative">
            <input
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrorMsg("");
              }}
              value={confirmPassword}
              className="w-full h-12 bg-transparent border-b border-gray-400 outline-none px-4 pr-10"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
            />
            <span
              className="absolute right-3 top-3 cursor-pointer text-gray-600"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <FaEyeSlash size={20} />
              ) : (
                <FaEye size={20} />
              )}
            </span>
          </div>

          {errorMsg && (
            <p className="text-sm text-red-500 font-semibold">{errorMsg}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={loading}
          className={`bg-secondColor hover:bg-black text-black-200 hover:text-white cursor-pointer w-full text-base font-medium h-10 rounded-md duration-300 flex items-center justify-center ${loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
        >
          {loading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-middle motion-reduce:animate-[spin_1.5s_linear_infinite]"></span>
          ) : (
            "Change Password"
          )}
        </button>
        <p className="text-sm text-center mt-4">
          Don't have an Account?{" "}
          <Link to="/signup" className="text-blue-600 hover:underline">
            Sign up
          </Link>
        </p>
      </form>
    </div>
  );
};

export default NewPassword;
